import styled from "@emotion/styled";
import { useCallback, useMemo, type ReactNode, type SyntheticEvent } from "react";

import {
  PropertiesPanelTab,
  PropertiesPanelTabs,
  stopClickPropagationProps,
  TabPanel,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { useFeatureSwitch } from "../application";
import { TriggersPropertiesView } from "../triggers/editors/TriggersPropertiesView";
import type { DesignerNodeHandle } from "../types";
import { useSelectedAspect, type SelectedNodeAspect } from "./useSelectedAspect";
import { Variations } from "./Variations";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const ScrollContainer = styled.div`
  height: 100%;
  flex: 1 1 100%;
  padding: 5px 0;
`;

const StyledTabPanel = styled(TabPanel)`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  font-size: ${({ theme }) => theme.fontSizeSmall};
  position: relative; // ElementStateIndicator.tsx has position:sticky, and aligns to this container.
`;

const PropertiesContainer = styled.div`
  // note: allow space for any material ui input label
  margin-top: 0.5rem;
`;

interface Props {
  node: DesignerNodeHandle;
  children: ReactNode;
}

export const NodePropertiesView = ({ node, children }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const [selectedAspect, onSelectedAspectChanged] = useSelectedAspect();

  const multilingualAvailable = useFeatureSwitch("multilingual");

  const aspects: SelectedNodeAspect[] = useMemo(
    () => (multilingualAvailable ? ["properties", "triggers", "variations"] : ["properties", "triggers"]),
    [multilingualAvailable],
  );

  const handleTabChange = useCallback(
    (e: SyntheticEvent, index: number) => {
      e.stopPropagation();
      onSelectedAspectChanged(aspects[index]);
    },
    [aspects, onSelectedAspectChanged],
  );

  // Fall back to Properties when the persisted aspect isn't a tab on this node (indexOf returns -1).
  const aspectIndex = aspects.indexOf(selectedAspect ?? "properties");
  const selectedTabIndex = aspectIndex === -1 ? 0 : aspectIndex;

  return (
    <Wrapper
      // Properties is portaled in from the details view,
      // so we need to stop propagation of mouse events to the main details area.
      {...stopClickPropagationProps}
    >
      <PropertiesPanelTabs
        value={selectedTabIndex}
        onChange={handleTabChange}
        data-test="tabs-container"
        color="primary"
        variant="fullWidth"
      >
        <Tooltip title={formatMessage("properties")} placement="top">
          <PropertiesPanelTab label={formatMessage("properties")} data-test="properties-tab" />
        </Tooltip>

        <Tooltip title={formatMessage("triggers")} placement="top">
          <PropertiesPanelTab label={formatMessage("triggers")} data-test="triggers-tab" />
        </Tooltip>

        {multilingualAvailable && (
          <Tooltip title={formatMessage("multilingual")} placement="top">
            <PropertiesPanelTab label={formatMessage("multilingual")} data-test="variations-tab" />
          </Tooltip>
        )}
      </PropertiesPanelTabs>

      <StyledTabPanel
        value={selectedTabIndex}
        index={aspects.indexOf("properties")}
        data-test="properties-tab-content-container"
        // Stay mounted while other tabs are active so canvas-interaction subscribers in the node's
        // properties view (drag/resize/nudge) keep working off the active tab.
        keepMounted
      >
        <ScrollContainer>
          <PropertiesContainer>{children}</PropertiesContainer>
        </ScrollContainer>
      </StyledTabPanel>

      <StyledTabPanel
        value={selectedTabIndex}
        index={aspects.indexOf("triggers")}
        data-test="triggers-tab-content-container"
      >
        <ScrollContainer>
          <PropertiesContainer>
            <TriggersPropertiesView node={node} />
          </PropertiesContainer>
        </ScrollContainer>
      </StyledTabPanel>

      <StyledTabPanel
        value={selectedTabIndex}
        index={aspects.indexOf("variations")}
        data-test="variations-tab-content-container"
      >
        <ScrollContainer>
          <Variations node={node} />
        </ScrollContainer>
      </StyledTabPanel>
    </Wrapper>
  );
};

const formatStrings = {
  properties: {
    en: "Properties",
  },
  multilingual: {
    en: "Variants",
  },
  triggers: {
    en: "Triggers",
  },
  interactions: {
    en: "Interactions",
  },
};
